import type { AnyFormApi } from '@tanstack/react-form';
import type { ToastOptions } from 'react-hot-toast';
import type { ZodError } from 'zod';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';

import toast from 'react-hot-toast';
import { batch } from '@tanstack/store';

import i18n from '../../i18n';

type BaseError = ZodError | string | ZodError[] | string[];

export type FormError = BaseError | Record<string, BaseError>;

/**
 * Sometimes reset form is not updating values correctly
 * and replaces new form values with the default ones.
 * This function resets the fields meta to remove form dirty and touched state.
 */
export const resetFormMeta = (form: AnyFormApi) => {
  if (!form?.fieldInfo) return;
  batch(() => {
    Object.keys(form.fieldInfo).forEach((field) => {
      form.setFieldMeta(field, (prev) => ({
        ...prev,
        isTouched: false,
        isDirty: false,
      }));
    });
  });
};

export const setFormSubmitErrors = (
  form: AnyFormApi,
  errors?: Record<string, string | string[]> | null,
) => {
  if (!errors) return;

  const filteredErrors: Record<string, string | string[]> = {};
  batch(() => {
    Object.entries(errors).forEach(([key, error]) => {
      if (form.fieldInfo[key]) {
        form.setFieldMeta(key, (prev) => ({
          ...prev,
          errorMap: { onSubmit: error },
          isTouched: true,
        }));
      } else {
        filteredErrors[key] = error;
      }
    });
  });

  return filteredErrors;
};

export const touchAllFields = (form: AnyFormApi) => {
  Object.values(form.fieldInfo).forEach((field: any) => {
    if (!field.instance) return;
    field.instance.setMeta((prev: any) => ({ ...prev, isTouched: true }));
  });
};

export const getFieldErrorMessage = (
  error: FormError | BaseError[],
): string => {
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) {
    return error.map((err) => getFieldErrorMessage(err)).join(', ');
  }
  if ('message' in error && typeof error.message === 'string')
    return error.message;

  return getFieldErrorMessage(
    Object.entries(error).map(([key, err]) =>
      key ? `${key}: ${getFieldErrorMessage(err)}` : getFieldErrorMessage(err),
    ),
  );
};

const getAllErrorMessages = (errors: Record<string, FormError>) => {
  if (!errors) return [];
  return Object.entries(errors).reduce(
    (messages, [key, error]) => {
      const fieldMessages = getFieldErrorMessage(error);
      if (fieldMessages) {
        messages.push({
          fieldLabel: key,
          message: fieldMessages,
        });
      }
      return messages;
    },
    [] as { fieldLabel: string; message: string }[],
  );
};

export const showValidationErrorToast = (
  errors?: Record<string, FormError> | null,
  title?: string,
) => {
  if (!errors || Object.keys(errors).length === 0) return null;
  const allErrors = getAllErrorMessages(errors);
  return toast(title || i18n.t('Form.TryAgain'), {
    duration: 5000,
    className: 'border-red',
    subMessage: (
      <>
        {allErrors.length > 0 && (
          <span className="flex flex-col gap-1 text-base mt-2">
            {allErrors.map(({ fieldLabel, message }, index) => (
              <span key={`${fieldLabel}-${message}-${index}`}>
                {fieldLabel && (
                  <span className="text-red-600 dark:text-red-400 font-bold">
                    {fieldLabel}:{' '}
                  </span>
                )}
                {message}
              </span>
            ))}
          </span>
        )}
      </>
    ),
  } as ToastOptions);
};

export const getUniqueEditors = (editors?: WebsocketAwarenessState[]) => {
  if (!editors) return [];
  const seenUserIds = new Set<string>();
  return editors?.filter(({ userId }) => {
    if (seenUserIds.has(userId)) {
      return false;
    }
    seenUserIds.add(userId);
    return true;
  });
};
